import { safeColor } from "../../../../engine/core/validation.js";
import {
  collapseAllIcon,
  collapseChildrenIcon,
  expandAllIcon,
  minusIcon,
  moreIcon,
  noteMarkerIcon,
  plusIcon,
} from "../icons.js";
import { escapeAttr, escapeHtml } from "../utils/html.js";
import { renderEmpty } from "./empty.js";
import { renderFocusCrumbs } from "./crumbs.js";
import { t } from "../../../../shared/i18n/index.js";
import { getLinkedBadge } from "../../views/linked-badge.js";
import { isInertLink } from "../../../../engine/sync/link-state.js";

/**
 * Virtualised outline.
 *
 * The outline used to render the whole visible tree as one nested HTML string.
 * On an 890-node document that is ~1.2 MB, and every structural edit re-parsed
 * and re-laid-out all of it — about a second on S7-class hardware, for a screen
 * that shows roughly 18 rows.
 *
 * Now only the rows near the viewport are rendered, with a spacer above and
 * below standing in for the rest so scroll height and scroll positions are
 * unchanged. Cost tracks the size of the screen, not the size of the document.
 *
 * Two things make this simpler than virtualisation usually is:
 *
 *  - Indentation comes from `--depth` on each item, not from DOM nesting
 *    (`.mobile-outline-children` carried no styling of its own), so the tree can
 *    be flattened to a list of siblings and look identical.
 *  - Rows are uniform height — measured at 34px on 888 of 889 rows — so row
 *    index maps to offset by multiplication, with no prefix-sum bookkeeping.
 *
 * ROW_H is re-measured from a real row after each render, so a font-size or
 * density change corrects itself on the next frame instead of being baked in.
 */

// Starting estimate; replaced by a real measurement as soon as a row exists.
let rowHeight = 34;

// Rows rendered beyond each edge of the viewport, so a fast flick shows content
// rather than blank space before the scroll handler catches up.
const OVERSCAN = 12;

export function getRowHeight() {
  return rowHeight;
}

/**
 * Re-measure row height from the DOM. Cheap (one getBoundingClientRect) and
 * self-correcting; called after each render.
 */
export function measureRowHeight(container) {
  // The root has a separator and extra bottom spacing. Sampling it made the
  // whole map use its taller height, then jump dozens of rows when a render
  // after scrolling finally measured a regular item instead.
  const row = container?.querySelector(".mobile-outline-item:not(.depth-0)");
  if (!row) return;
  const h = row.getBoundingClientRect().height;
  // Ignore nonsense (display:none, mid-animation). A zero here would poison
  // every subsequent window calculation.
  if (h >= 12 && h <= 200) rowHeight = h;
}

/**
 * Flatten the tree to the rows that are actually visible, in display order.
 *
 * Keeps otherwise tag-hidden ancestors when a descendant remains visible.
 * Hiding Done still hides the entire completed subtree, like the map views.
 * Skips the children of collapsed nodes. Highlighting does NOT remove rows — it
 * marks non-matching ones (that also have no matching descendant) with
 * `dim: true`, and the row renders faded via --tag-dim-amount, matching the
 * spotlight behavior of the map views. Explicit `collapsed` always wins —
 * except at the depth-0 anchor (document root or focused node), which is
 * always expanded like in the map views. Starts from the focused node when
 * focus is set.
 */
export function flattenVisibleRows(appState, deps) {
  const { callbacks, isHiddenByTag, nodeMatchesTag } = deps;
  const highlightedIds = appState.highlightedTagIds || [];
  const hiddenIds = appState.hiddenTagIds || [];
  const doneTagId = callbacks.sharedCallbacks?.getDoneTagId?.();
  const doneHidden = appState.hideCompleted || (doneTagId && hiddenIds.includes(doneTagId));
  const filtered = doneHidden || hiddenIds.length || highlightedIds.length;
  const rows = [];

  // One read-only pass per flatten. Cache both visibility and highlight paths
  // together so deep trees do not repeatedly scan the same descendants. Look
  // through folds for matching descendants, but keep folds intact when drawing.
  const summaries = new Map();
  const summarize = (node) => {
    if (!filtered) return { visible: true, match: false };
    if (summaries.has(node)) return summaries.get(node);
    if (doneHidden && (node.status === "done" || callbacks.sharedCallbacks?.nodeIsDone?.(node))) {
      const result = { visible: false, match: false };
      summaries.set(node, result);
      return result;
    }
    const selfVisible = !isHiddenByTag(node, appState);
    let visible = selfVisible;
    let match = selfVisible && nodeMatchesTag(node, highlightedIds);
    for (const child of node.children || []) {
      if (!child) continue;
      const result = summarize(child);
      visible ||= result.visible;
      match ||= result.match;
    }
    const result = { visible, match };
    summaries.set(node, result);
    return result;
  };

  const walk = (node, depth) => {
    if (!node) return;
    const { visible, match } = summarize(node);
    if (!visible) return;
    const dim = highlightedIds.length > 0 && !match;

    rows.push({ node, depth, dim });

    const hasChildren = !!node.children?.length;
    // `collapsed` always wins — an active highlight filter used to
    // force-expand everything, which made every fold button silently
    // inert (icon didn't even flip). Hidden branches without visible
    // descendants are pruned; matches under a collapsed branch are reachable by
    // expanding it, exactly what the user asked the button to control.
    //
    // Exception: the depth-0 anchor (document root or focused node) is
    // always expanded, matching the map views — focusing a node you
    // previously collapsed must show its children, not a single dead row
    // whose fold/unfold buttons appear to act on the wrong scope.
    if (hasChildren && (depth === 0 || !node.collapsed)) {
      for (const child of node.children) walk(child, depth + 1);
    }
  };

  // The outline follows focus like the map views: render the focused
  // subtree, not always the whole document.
  let start = appState.data;
  if (appState.focusId && appState.focusId !== appState.data?.id) {
    const focused = callbacks?.Store?.find?.(appState.focusId);
    if (focused) start = focused;
  }
  walk(start, 0);
  return rows;
}

/**
 * Which slice of the flattened rows to render for a given scroll position.
 */
export function computeWindow(total, scrollTop, viewportH) {
  const h = rowHeight || 34;
  const visible = Math.ceil((viewportH || 600) / h);
  // Filtering/folding can shorten a deeply scrolled list. Clamp to its new
  // end before creating spacers; an oversized top spacer would otherwise keep
  // the stale offset alive while slice(first, last) produces no rows at all.
  const top = Math.min(Math.max(0, Math.floor(scrollTop / h)), Math.max(0, total - visible));
  const first = Math.max(0, top - OVERSCAN);
  const last = Math.min(total, first + visible + OVERSCAN * 2);
  return { first, last };
}

/**
 * Render the row window plus its spacers. Used for both the initial render and
 * the in-place patch on scroll, so the two cannot drift apart.
 */
export function renderRowWindow(rows, appState, deps, first, last) {
  const before = first * rowHeight;
  const after = Math.max(0, (rows.length - last) * rowHeight);
  const body = rows
    .slice(first, last)
    .map(({ node, depth, dim }) => renderOutlineRow(node, depth, appState, deps, dim))
    .join("");
  // aria-hidden: the spacers are layout, not content — a screen reader
  // announcing two empty regions around the rows would just be noise.
  return (
    `<div class="mobile-outline-spacer" aria-hidden="true" style="height:${before}px"></div>`
    + body
    + `<div class="mobile-outline-spacer" aria-hidden="true" style="height:${after}px"></div>`
  );
}

export function renderOutline(appState, deps) {
  const { callbacks, uiState } = deps;
  const root = appState.data;
  if (!root) {
    return `
      <section class="mobile-view mobile-view-padded">
        ${renderEmpty(t("mobile.empty.noDocument"))}
        <button class="mobile-primary-btn" data-mobile-action="create-doc">${escapeHtml(t("mobile.outline.createDocument"))}</button>
      </section>
    `;
  }

  const activeFilters =
    appState.hideCompleted ||
    (appState.highlightedTagIds?.length || 0) ||
    (appState.hiddenTagIds?.length || 0);

  const rows = flattenVisibleRows(appState, deps);
  const { first, last } = computeWindow(
    rows.length,
    uiState.outlineScrollTop || 0,
    uiState.outlineViewportH || 0,
  );

  const readOnly = callbacks.Store.isReadOnly?.();
  return `
    <section class="mobile-view">
      <div class="mobile-visual-toolbar mobile-outline-toolbar">
        <div class="mobile-focus-strip in-toolbar">
          ${renderFocusCrumbs(appState, callbacks, { bare: true })}
        </div>
        ${readOnly ? "" : `
          <div class="mobile-visual-controls">
            <span class="mobile-toolbar-sep" aria-hidden="true"></span>
            <button class="mobile-icon-btn compact"
                    data-mobile-action="expand-all-nodes"
                    aria-label="${escapeAttr(t("mobile.fold.unfoldOne"))}"
                    title="${escapeAttr(t("mobile.fold.unfoldOne"))}">${expandAllIcon()}</button>
            <button class="mobile-icon-btn compact"
                    data-mobile-action="collapse-all-nodes"
                    aria-label="${escapeAttr(t("mobile.fold.foldFirst"))}"
                    title="${escapeAttr(t("mobile.fold.foldFirst"))}">${collapseAllIcon()}</button>
          </div>
        `}
      </div>
      <div class="mobile-outline" data-mobile-action="outline-deselect" data-virtualised="1"
           data-window-first="${first}" data-window-last="${last}" data-row-height="${rowHeight}">
        ${rows.length
          ? renderRowWindow(rows, appState, deps, first, last)
          : renderEmpty(activeFilters ? t("mobile.outline.noMatch") : t("mobile.outline.empty"))}
      </div>
    </section>
  `;
}

/**
 * One row. Identical markup to the old recursive renderer, minus the
 * `.mobile-outline-children` wrapper — that existed only to nest, and depth is
 * expressed by `--depth`.
 */
function renderOutlineRow(node, depth, appState, deps, dim = false) {
  const {
    callbacks,
    uiState,
    getActiveDocIdForNotes,
    getInlineRenameDraft,
    getNodeTagLabels,
    getSafeStatus,
    renderTagChip,
  } = deps;

  const hasChildren = !!node.children?.length;
  // The depth-0 anchor row is force-expanded by the flatten, so its own
  // fold button would be a lie — render the placeholder instead. Use the
  // global one-level fold buttons in the toolbar for that row's children.
  const foldable = hasChildren && depth > 0;
  const expanded = hasChildren && !node.collapsed;
  const status = getSafeStatus(node);
  const tags = getNodeTagLabels(node);
  const hasNotes = !!(node.notes && String(node.notes).trim());
  // Depth 0 is whatever the flatten started from — the document root or the
  // focused node. Either way it's the row that anchors the view: styled as
  // root, not swipe-deletable.
  const isRoot = depth === 0;
  const selected = node.id === appState.selectedId;
  const readOnly = callbacks.Store.isReadOnly?.();
  // This node: also read-only in a linked map the user can only view, and a
  // link showing no content here cannot be renamed (desktop does the same).
  const nodeReadOnly = readOnly || isInertLink(node) || !!callbacks.sharedCallbacks?.isNodeReadOnly?.(node.id);
  const link = getLinkedBadge(node);
  // Short-circuit: only the row being renamed needs a draft lookup.
  const inlineDraft = uiState.editingTitleNodeId === node.id
    ? getInlineRenameDraft({ docId: getActiveDocIdForNotes(), nodeId: node.id })
    : null;
  const titleValue = inlineDraft ? inlineDraft.name : (node.name || "");

  return `
    <div class="mobile-outline-item depth-${Math.min(depth, 6)}" style="--depth:${depth}">
      <div class="mobile-outline-row ${isRoot ? "root" : ""} ${selected ? "selected" : ""} ${dim ? "tag-dim" : ""}"
           data-mobile-action="select-outline-node"
           data-node-id="${escapeAttr(node.id)}">
        <button class="mobile-expand-btn ${foldable ? "" : "placeholder"}"
                ${foldable
                  ? `data-mobile-action="toggle-collapse" data-node-id="${escapeAttr(node.id)}" aria-expanded="${expanded}"`
                  : `aria-hidden="true" tabindex="-1"`}
                aria-label="${escapeAttr(t(expanded ? "vault.folder.collapse" : "vault.folder.expand", { name: node.name || t("mobile.outline.nodeFallback") }))}">
          ${foldable ? (expanded ? minusIcon() : plusIcon()) : ""}
        </button>
        <span class="mobile-status-dot ${status}" style="--node-color:${safeColor(node.color, "#6b7280")}"></span>
        <span class="mobile-outline-main">
          <span class="mobile-outline-title-line">
            ${uiState.editingTitleNodeId === node.id && !nodeReadOnly
              ? `<input class="mobile-outline-title-input"
                        data-mobile-rename-input
                        data-node-id="${escapeAttr(node.id)}"
                        value="${escapeAttr(titleValue)}" />`
              : `<span class="mobile-outline-title"
                       data-mobile-action="title-tap"
                       data-node-id="${escapeAttr(node.id)}">${escapeHtml(node.name || t("common.untitled"))}</span>`}
            ${link.isLinkedStub ? `<button type="button" class="${escapeAttr(link.className)} mobile-link-badge"
                    data-mobile-action="link-badge" data-node-id="${escapeAttr(node.id)}"
                    title="${escapeAttr(link.title)}" aria-label="${escapeAttr(link.title)}"><span aria-hidden="true">${escapeHtml(link.icon)}</span>${escapeHtml(link.label)}</button>` : ""}
            ${tags.length ? `<span class="mobile-tag-row inline">${tags.map(renderTagChip).join("")}</span>` : ""}
            ${hasNotes ? `<span class="mobile-note-marker" aria-label="${escapeAttr(t("mobile.outline.hasNotes"))}" title="${escapeAttr(t("mobile.outline.hasNotes"))}">${noteMarkerIcon()}</span>` : ""}
            ${hasChildren ? `<span class="mobile-child-badge" aria-label="${escapeAttr(t("mobile.node.childCount", { count: node.children.length }))}">${node.children.length}</span>` : ""}
          </span>
        </span>
        <span class="mobile-outline-actions">
          ${readOnly || !hasChildren ? "" : `
            <button class="mobile-row-action compact mobile-selected-only"
                    data-mobile-action="collapse-selected-children"
                    data-node-id="${escapeAttr(node.id)}"
                    aria-label="${escapeAttr(t("mobile.outline.foldChildrenAria"))}"
                    title="${escapeAttr(t("mobile.outline.foldChildrenTitle"))}">${collapseChildrenIcon()}</button>
          `}
          <button class="mobile-row-action compact"
                  data-mobile-action="open-node-properties"
                  data-node-id="${escapeAttr(node.id)}"
                  aria-label="${escapeAttr(t("mobile.node.actions"))}"
                  title="${escapeAttr(t("mobile.node.actions"))}">${moreIcon()}</button>
        </span>
      </div>
    </div>
  `;
}
